import React, { useMemo } from 'react';
import { Table, TableColumnType, Tag, Button, Image } from 'antd';
import dayjs from 'dayjs';
import { StyleRecord } from '../types';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { getStatusInfo, formatYesNo } from '../utils';
import { TABLE_CONFIG } from '../constants';

interface StyleTableProps {
  data: StyleRecord[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onEdit: (record: StyleRecord) => void;
}

const StyleTable: React.FC<StyleTableProps> = ({
  data,
  loading,
  pagination,
  onEdit,
}) => {
  const isMobile = useAtomValue(isMobileAtom);

  const columns = useMemo<TableColumnType<StyleRecord>[]>(
    () => [
      {
        title: 'ID',
        dataIndex: 'styleId',
        key: 'styleId',
        width: 80,
      },
      {
        title: '风格图片',
        dataIndex: 'styleImage',
        key: 'styleImage',
        width: 120,
        render: (imageUrl: string) =>
          imageUrl ? (
            <Image
              src={imageUrl}
              alt="风格图片"
              width={TABLE_CONFIG.ICON_SIZE}
              height={TABLE_CONFIG.ICON_SIZE}
              style={{ objectFit: 'cover' }}
              preview={{ mask: '预览' }}
            />
          ) : (
            '-'
          ),
      },
      {
        title: '风格名称',
        dataIndex: 'styleName',
        key: 'styleName',
        width: 150,
      },
      {
        title: '风格名称（日文）',
        dataIndex: 'styleNameJa',
        key: 'styleNameJa',
        width: 150,
      },
      {
        title: '风格顺序',
        dataIndex: 'styleOrder',
        key: 'styleOrder',
        width: 100,
      },
      {
        title: '是否生效',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: number) => {
          const { text, color } = getStatusInfo(status);
          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: '是否期间限定',
        dataIndex: 'limitTime',
        key: 'limitTime',
        width: 140,
        render: (limitTime: number) => formatYesNo(limitTime),
      },
      {
        title: '是否推荐',
        dataIndex: 'isRecommend',
        key: 'isRecommend',
        width: 100,
        render: (isRecommend?: number) =>
          isRecommend !== undefined ? formatYesNo(isRecommend) : '-',
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      // {
      //   title: '更新时间',
      //   dataIndex: 'updateTime',
      //   key: 'updateTime',
      //   width: 180,
      //   render: (time: string) =>
      //     time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      // },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: isMobile ? 'right' : undefined,
        render: (_, record) => (
          <Button type="link" onClick={() => onEdit(record)}>
            编辑
          </Button>
        ),
      },
    ],
    [isMobile, onEdit]
  );

  return (
    <Table<StyleRecord>
      columns={columns}
      dataSource={data}
      rowKey="styleId"
      loading={loading}
      scroll={{ x: isMobile ? 1200 : undefined }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        onShowSizeChange: pagination.onChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: TABLE_CONFIG.PAGE_SIZE_OPTIONS,
      }}
      size={isMobile ? 'small' : 'middle'}
    />
  );
};

export default StyleTable;

